import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import { getPrescription, viewPrescriptionById } from "../API/PrescriptionApi";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, Grid2, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { formatAge } from "../Utility/Utility";

function PhysiotherapyPrescriptionReadOnly({ serviceId, prescriptionId }) {
  const [errorMessage, setErrorMessage] = useState();
  const { auth } = useContext(AuthContext);
  const [prescriptionStatus, setPrescriptionStatus] = useState();
  const [currentService, setCurrentService] = useState();
  
  // Prescription data
  const [complaint, setComplaint] = useState();
  const [medicalHistoryNotes, setMedicalHistoryNotes] = useState();
  const [medicalHistoryConditions, setMedicalHistoryConditions] = useState([]);
  const [surgeryDetails, setSurgeryDetails] = useState();
  const [painLevel, setPainLevel] = useState();
  const [painTypes, setPainTypes] = useState([]);
  const [rangeOfMotion, setRangeOfMotion] = useState();
  const [muscleStrength, setMuscleStrength] = useState();
  const [muscleTightness, setMuscleTightness] = useState();
  const [specialTests, setSpecialTests] = useState();
  const [treatmentPlan, setTreatmentPlan] = useState();
  const [dosDonts, setDosDonts] = useState();
  const [suggestedSessions, setSuggestedSessions] = useState();
  const [shortTermTreatmentGoals, setShortTermTreatmentGoals] = useState();
  const [longTermTreatmentGoals, setLongTermTreatmentGoals] = useState();
  const [treatmentMethods, setTreatmentMethods] = useState([]);

  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        var result = await getServiceDetails(auth, serviceId);
        if (result?.isSuccess) {
          setCurrentService(result?.data);
        } else {
          showSnackbar(result?.data, "error");
        }
      }
    };

    const loadPrescription = async () => {
      if (auth != null && prescriptionId != null) {
        var result = await getPrescription(auth, prescriptionId);
        if (result.isSuccess) {
          var prescription = result?.data;
          setPrescriptionStatus(prescription?.status);
          var detailedPrescriptionJson = JSON.parse(
            prescription?.detailedPrescription
          );
          if (prescription?.detailedPrescription) {
            setComplaint(detailedPrescriptionJson?.complaint);
            setMedicalHistoryConditions(detailedPrescriptionJson?.medicalHistoryConditions);
            setMedicalHistoryNotes(detailedPrescriptionJson?.medicalHistoryNotes);
            setSurgeryDetails(detailedPrescriptionJson?.surgeryDetails);
            setPainLevel(detailedPrescriptionJson?.painLevel);
            setPainTypes(detailedPrescriptionJson?.painTypes);
            setRangeOfMotion(detailedPrescriptionJson?.rangeOfMotion);
            setMuscleStrength(detailedPrescriptionJson?.muscleStrength);
            setMuscleTightness(detailedPrescriptionJson?.muscleTightness);
            setSpecialTests(detailedPrescriptionJson?.specialTests);
            setTreatmentPlan(detailedPrescriptionJson?.treatmentPlan);
            setDosDonts(detailedPrescriptionJson?.dosDonts);
            setSuggestedSessions(detailedPrescriptionJson?.suggestedSessions);
            setShortTermTreatmentGoals(detailedPrescriptionJson?.shortTermTreatmentGoals);
            setLongTermTreatmentGoals(detailedPrescriptionJson?.longTermTreatmentGoals);
            setTreatmentMethods(detailedPrescriptionJson?.treatmentMethods);
          }
        } else {
          showSnackbar(result?.data, "error");
        }
      }
    };
    
    loadService();
    loadPrescription();
  }, [auth, serviceId, prescriptionId]);

  const viewPrescription = async () => {
    if (auth != null && prescriptionId != null) {
      await viewPrescriptionById(auth, prescriptionId);
    }
  };

  // Helper function to get selected items as a comma-separated string
  const getSelectedItems = (items) => {
    if (!items) return "";
    return items
      .filter(item => item.selected)
      .map(item => item.displayValue)
      .join(", ");
  };

  return (
    <>
      <Grid2 container size={12} sx={{ marginX: "10px", marginY: "10px" }}>
        <Grid2 container size={12}>
          <Grid2 container size={4} spacing={1} alignContent="center">
            {prescriptionStatus === "Final" && (
              <Tooltip title="Download as PDF">
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={viewPrescription}
                >
                  Download
                </Button>
              </Tooltip>
            )}
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2
            size={{ sx: 12, sm: 12, md: 12, lg: 9, xl: 9 }}
            sx={{ paddingTop: "10px" }}
          >
            <Card
              variant="outlined"
              fullWidth
              sx={{ width: "100%", padding: "10px" }}
            >
              <Grid2 container spacing={1} size={12} sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto"}}>
                <Grid2 container size={12}>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Patient Name</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                        {currentService?.patientPrefix +
                          " " +
                          currentService?.patientFirstName +
                          " " +
                          currentService?.patientLastName}
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Age and Gender</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                        {formatAge(currentService?.patientAgeInYears)} and{" "}
                                                   {currentService?.patientGender}
                      </Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>MRN</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>{currentService?.patientMRN}</Typography>
                    </Grid2>
                  </Grid2>
                </Grid2>
                <Grid2 container size={12}>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Visit Id</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>{currentService?.visitDisplayId}</Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Visit Type</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography></Typography>
                    </Grid2>
                  </Grid2>
                  <Grid2 size={4}>
                    <Grid2 size={12}>
                      <Typography sx={{textDecoration: 'underline'}}>Date Time</Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography>
                        {dayjs().format("DD-MMM-YYYY hh:mm A")}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Grid2>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography component="span">
                      Complaint and Medical History
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Chief Complaint</Typography>
                        <Typography variant="body1" paragraph>{complaint}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Medical History</Typography>
                        <Typography variant="body1" paragraph>
                          {getSelectedItems(medicalHistoryConditions)}
                        </Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Medical History Notes</Typography>
                        <Typography variant="body1" paragraph>{medicalHistoryNotes}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Surgery Details</Typography>
                        <Typography variant="body1" paragraph>{surgeryDetails}</Typography>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography component="span">Assessment</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Pain Level</Typography>
                        <Typography variant="body1" paragraph>{painLevel} / 10</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Pain Types</Typography>
                        <Typography variant="body1" paragraph>{getSelectedItems(painTypes)}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Range of Motion</Typography>
                        <Typography variant="body1" paragraph>{rangeOfMotion}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Muscle Strength</Typography>
                        <Typography variant="body1" paragraph>{muscleStrength}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Muscle spasm \ Tightness \ Trigger points</Typography>
                        <Typography variant="body1" paragraph>{muscleTightness}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Special Test</Typography>
                        <Typography variant="body1" paragraph>{specialTests}</Typography>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded variant="outlined" sx={{ width: "100%", maxWidth: "100%" }}>
                  <AccordionSummary
                    expandIcon={<GridExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                  >
                    <Typography component="span">Prescribed Treatment</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px", position: "relative" }}>
                    <Grid2 container spacing={3} size={12} sx={{justifyContent:'center', paddingX:'10px'}}>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Treatment Plan</Typography>
                        <Typography variant="body1" paragraph>{treatmentPlan}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Suggested no. of sessions</Typography>
                        <Typography variant="body1" paragraph>{suggestedSessions}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Short term treatment goals</Typography>
                        <Typography variant="body1" paragraph>{shortTermTreatmentGoals}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Treatment Methods</Typography>
                        <Typography variant="body1" paragraph>
                          {treatmentMethods
                            ?.filter(method => method.selected)
                            .map(method => {
                              if (method.value === "Others" && method.additionalText) {
                                return `${method.displayValue} (${method.additionalText})`;
                              }
                              return method.displayValue;
                            })
                            .join(", ")}
                        </Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Long term treatment goals</Typography>
                        <Typography variant="body1" paragraph>{longTermTreatmentGoals}</Typography>
                      </Grid2>
                      <Grid2 size={12}>
                        <Typography variant="subtitle1" sx={{fontWeight: 'bold', mb: 1}}>Do's and Dont's</Typography>
                        <Typography variant="body1" paragraph>{dosDonts}</Typography>
                      </Grid2>
                    </Grid2>
                  </AccordionDetails>
                </Accordion>
              </Grid2>
            </Card>
          </Grid2>
        </Grid2>
      </Grid2>
    </>
  );
}

export default PhysiotherapyPrescriptionReadOnly;